// src/pages/jobs/JobCreate.js
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import notificationApi from '../../services/notificationApi'
import { useGlobal } from '../../context/GlobalContext'
import '../../assets/css/JobCreate.css'

function JobCreate() {
  console.log('[JobCreate] Rendering')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [sites, setSites] = useState([])
  const [siteId, setSiteId] = useState('')
  const [locations, setLocations] = useState([])
  const [filteredLocations, setFilteredLocations] = useState([])
  const [truckers, setTruckers] = useState([])
  const [existingTruckerId, setExistingTruckerId] = useState('')
  const [steps, setSteps] = useState([])
  const navigate = useNavigate()
  const { showLoading, hideLoading, showMessage } = useGlobal()

  useEffect(() => {
    console.log('[JobCreate] useEffect -> loadAllData')
    loadAllData()
  }, [])

  async function loadAllData() {
    console.log('[JobCreate] loadAllData called')
    showLoading()
    try {
      await loadSites()
      await loadLocations()
      await loadAvailableTruckers()
    } catch (error) {
      console.log('[JobCreate] Error in loadAllData:', error)
      showMessage('Error loading data', 'error')
    } finally {
      hideLoading()
    }
  }

  async function loadSites() {
    console.log('[JobCreate] loadSites called')
    const res = await api.get('/site/all')
    console.log('[JobCreate] Sites:', res.data)
    if (res.data && res.data.sites) setSites(res.data.sites)
  }

  async function loadLocations() {
    console.log('[JobCreate] loadLocations called')
    const res = await api.get('/location/company-locations')
    console.log('[JobCreate] Locations:', res.data)
    if (res.data && res.data.locations) setLocations(res.data.locations)
  }

  async function loadAvailableTruckers() {
    console.log('[JobCreate] loadAvailableTruckers called')
    const res = await api.get('/trucker/available-truckers')
    console.log('[JobCreate] Truckers:', res.data)
    if (res.data && res.data.truckers) setTruckers(res.data.truckers)
  }

  function handleSiteChange(e) {
    console.log('[JobCreate] Site change:', e.target.value)
    setSiteId(e.target.value)
    if (!e.target.value) {
      setFilteredLocations([])
      return
    }
    const filtered = locations.filter((r) => r.site && r.site._id === e.target.value)
    console.log('[JobCreate] Filtered locations:', filtered)
    setFilteredLocations(filtered)
  }

  function addStep() {
    console.log('[JobCreate] addStep called')
    setSteps((prev) => [
      ...prev,
      {
        locationId: '',
        dueTime: '',
        reminderBefore: '30',
        reminderInterval: '15',
        isActive: false
      }
    ])
  }

  function removeStep(index) {
    console.log('[JobCreate] removeStep called, index:', index)
    setSteps((prev) => prev.filter((_, i) => i !== index))
  }

  function handleStepChange(index, field, value) {
    console.log('[JobCreate] handleStepChange -> index:', index, 'field:', field, 'value:', value)
    setSteps((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    console.log('[JobCreate] handleSubmit called')
    showLoading()
    try {
      const payload = {
        title,
        description,
        siteId,
        existingTruckerId,
        jobCreationMsg: '',
        steps: steps.map((s, i) => ({
          locationId: s.locationId || '',
          dueTime: s.dueTime || '',
          reminderBefore: s.reminderBefore || '30',
          reminderInterval: s.reminderInterval || '15',
          message: '',
          isActive: i === 0
        }))
      }
      console.log('[JobCreate] Sending payload:', payload)
      const createdJobRes = await api.post('/jobs/create', payload)
      console.log('[JobCreate] create response:', createdJobRes.data)
      if (createdJobRes.data && createdJobRes.data.jobId) {
        await notificationApi.post('/notifications/job-created', {
          jobId: createdJobRes.data.jobId
        })
        console.log('[JobCreate] Notification sent')
        showMessage('Job created successfully', 'success')
        navigate('/dashboard')
      } else {
        console.log('[JobCreate] Failed to create job')
        showMessage('Failed to create job', 'error')
      }
    } catch (err) {
      console.log('[JobCreate] Error creating job:', err)
      showMessage('Error creating job', 'error')
    } finally {
      hideLoading()
    }
  }

  console.log('[JobCreate] Returning JSX')
  return (
    <div className="job-page-container">
      <h2 className="job-title">Create a New Job</h2>
      <form onSubmit={handleSubmit} className="job-form">
        <div className="job-form-group">
          <label className="job-label">Title</label>
          <input
            className="job-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div className="job-form-group">
          <label className="job-label">Description</label>
          <textarea
            className="job-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="job-form-group">
          <label className="job-label">Select Site</label>
          <select className="job-select" value={siteId} onChange={handleSiteChange}>
            <option value="">-- Choose --</option>
            {sites.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.plainName} - {loc.address}
              </option>
            ))}
          </select>
        </div>
        <div className="job-form-group">
          <label className="job-label">Select Trucker</label>
          <select
            className="job-select"
            value={existingTruckerId}
            onChange={(e) => setExistingTruckerId(e.target.value)}
          >
            <option value="">-- Choose --</option>
            {truckers.map((t) => (
              <option key={t._id} value={t._id}>
                {t.plainName} {t.plainSurname} - {t.plainLicensePlateTruck}
              </option>
            ))}
          </select>
        </div>
        <h3 className="job-steps-title">Steps</h3>
        <button type="button" className="job-btn" onClick={addStep}>
          Add Step
        </button>
        {steps.map((step, index) => (
          <div key={index} className="job-step-container">
            <label className="job-step-label">Step #{index + 1}</label>
            <div className="job-form-group">
              <label className="job-label">Location</label>
              <select
                className="job-select"
                value={step.locationId}
                onChange={(e) => handleStepChange(index, 'locationId', e.target.value)}
              >
                <option value="">-- Choose --</option>
                {filteredLocations.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.plainLocationName} ({r.address})
                  </option>
                ))}
              </select>
            </div>
            <div className="job-form-group">
              <label className="job-label">Due Time</label>
              <input
                className="job-input"
                type="datetime-local"
                value={step.dueTime}
                onChange={(e) => handleStepChange(index, 'dueTime', e.target.value)}
              />
            </div>
            <div className="job-form-group">
              <label className="job-label">Reminder Before (minutes)</label>
              <input
                className="job-input"
                type="number"
                value={step.reminderBefore}
                onChange={(e) => handleStepChange(index, 'reminderBefore', e.target.value)}
              />
            </div>
            <div className="job-form-group">
              <label className="job-label">Reminder Interval (minutes)</label>
              <input
                className="job-input"
                type="number"
                value={step.reminderInterval}
                onChange={(e) => handleStepChange(index, 'reminderInterval', e.target.value)}
              />
            </div>
            <button
              type="button"
              className="job-btn job-remove-step"
              onClick={() => removeStep(index)}
            >
              Remove Step
            </button>
          </div>
        ))}
        <button type="submit" className="job-btn job-submit">
          Create Job
        </button>
      </form>
    </div>
  )
}

export default JobCreate